import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSessionStatsList, getAggregatedStats, getDrillHistory } from '../lib/firestore';
import { calculateTier, TIERS, progressToNextTier, DRILLS, DRILL_TIER_CONFIGS, getDrillTier, getNextDrillTier } from '../shared';
import type { SessionStats, AggregatedStats, DrillAttempt, DrillType } from '../shared';
import { Panel, TierBadge, LoadingSpinner, EmptyState, StatCard } from '../components/ui/Cards';
import { Button } from '../components/ui/Button';
import { DRILL_ICONS } from '../drillIcons';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

type Tab = 'sessions' | 'drills';

export function ProgressPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]                   = useState<Tab>('sessions');
  const [sessionStats, setSessionStats] = useState<SessionStats[]>([]);
  const [aggStats, setAggStats]         = useState<AggregatedStats | null>(null);
  const [drillHistory, setDrillHistory] = useState<DrillAttempt[]>([]);
  const [loading, setLoading]           = useState(true);
  const [selectedDrill, setSelectedDrill] = useState<DrillType | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getSessionStatsList(user.uid, 30),
      getAggregatedStats(user.uid),
      getDrillHistory(user.uid),
    ]).then(([s, agg, drills]) => {
      setSessionStats(s.reverse());
      setAggStats(agg);
      setDrillHistory(drills);
      setLoading(false);
    });
  }, [user]);

  if (loading) return <LoadingSpinner />;

  const avgBreak   = aggStats?.avgBreak30d   ?? 0;
  const potRate    = aggStats?.avgPotRate30d  ?? 0;
  const safetyRate = aggStats?.avgSafetyRate30d ?? 0;
  const tier       = calculateTier(avgBreak, potRate, safetyRate);
  const { nextTier, gaps } = progressToNextTier(avgBreak, potRate, safetyRate);

  // ── Chart data — core stats ────────────────────────────────────
  const chartData = sessionStats.map((s, i) => ({
    session: i + 1,
    potRate:    s.potRate,
    highBreak:  s.highBreak,
    avgBreak:   s.avgBreak,
    safetyRate: s.safetyRate,
  }));

  // ── Chart data — specialty stats (only sessions with data) ─────
  const longChartData = sessionStats
    .map((s, i) => ({
      session: i + 1,
      longPotRate:      (s as any).totalLongAttempts  > 0 ? (s as any).longPotRate      : null,
      restSuccessRate:  (s as any).totalRestAttempts  > 0 ? (s as any).restSuccessRate  : null,
      snookerEscapeRate:(s as any).snookeredCount     > 0 ? (s as any).snookerEscapeRate : null,
    }));

  const hasLongData    = sessionStats.some(s => ((s as any).totalLongAttempts  ?? 0) > 0);
  const hasRestData    = sessionStats.some(s => ((s as any).totalRestAttempts  ?? 0) > 0);
  const hasEscapeData  = sessionStats.some(s => ((s as any).snookeredCount     ?? 0) > 0);
  const hasSpecialty   = hasLongData || hasRestData || hasEscapeData;

  // ── Aggregate specialty totals for summary cards ───────────────
  const totalLongAttempts  = sessionStats.reduce((n, s) => n + ((s as any).totalLongAttempts  ?? 0), 0);
  const totalLongPots      = sessionStats.reduce((n, s) => n + ((s as any).totalLongPots      ?? 0), 0);
  const totalRestAttempts  = sessionStats.reduce((n, s) => n + ((s as any).totalRestAttempts  ?? 0), 0);
  const totalRestPots      = sessionStats.reduce((n, s) => n + ((s as any).totalRestPots      ?? 0), 0);
  const totalSnookered     = sessionStats.reduce((n, s) => n + ((s as any).snookeredCount     ?? 0), 0);
  const totalEscapes       = sessionStats.reduce((n, s) => n + ((s as any).snookerEscapes     ?? 0), 0);

  const overallLongRate   = totalLongAttempts  > 0 ? Math.round((totalLongPots  / totalLongAttempts)  * 100) : null;
  const overallRestRate   = totalRestAttempts  > 0 ? Math.round((totalRestPots  / totalRestAttempts)  * 100) : null;
  const overallEscapeRate = totalSnookered     > 0 ? Math.round((totalEscapes   / totalSnookered)      * 100) : null;

  // ── Drills ─────────────────────────────────────────────────────
  const drillSummary = DRILLS.map(drill => {
    const attempts = drillHistory.filter(d => d.drillId === drill.id);
    const scores   = attempts.map(a => Number(a.notes ?? 0)).filter(n => !isNaN(n));
    const best     = scores.length > 0 ? Math.max(...scores) : null;
    const avg      = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    const bestTier    = best !== null ? getDrillTier(drill.id, best) : null;
    const nextDrillTier = best !== null ? getNextDrillTier(drill.id, best) : null;
    return { drill, attempts: attempts.length, best, avg, bestTier, nextDrillTier, history: attempts.slice(0, 20) };
  }).filter(d => d.attempts > 0);

  const hasDrillData   = drillSummary.length > 0;
  const activeSelected = selectedDrill ?? (drillSummary[0]?.drill.id ?? null);
  const activeDrillData = drillSummary.find(d => d.drill.id === activeSelected);
  const activeDrillChart = activeDrillData
    ? activeDrillData.history.slice().reverse().map((a, i) => ({ attempt: i + 1, score: Number(a.notes ?? 0) }))
    : [];

  if (sessionStats.length === 0 && !hasDrillData) {
    return (
      <EmptyState
        icon=""
        title="No data yet"
        subtitle="Log a few sessions to see your progress charts."
        action={<Button onClick={() => navigate('/session')}>Log First Session</Button>}
      />
    );
  }

  return (
    <div className="page u-fade-up">
      <div className="page__header">
        <div>
          <h1 className="page__title">Progress</h1>
          <p className="page__sub">Your last {sessionStats.length} sessions — 30-day rolling averages.</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {(['sessions', 'drills'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none',
            borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent',
            color: tab === t ? 'var(--gold)' : 'var(--chalk-faint)',
            fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '8px 20px', cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s',
          }}>
            {t === 'sessions' ? 'Sessions' : 'Drills'}
          </button>
        ))}
      </div>

      {/* ══════════════ SESSIONS TAB ══════════════ */}
      {tab === 'sessions' && (
        <>
          {sessionStats.length === 0 ? (
            <EmptyState icon="" title="No session data yet" subtitle="Log your first session to see charts."
              action={<Button onClick={() => navigate('/session')}>Log Session</Button>} />
          ) : (
            <>
              {/* ── Summary stat cards ── */}
              <div className="u-stats-grid">
                <StatCard label="Pot Rate (30d)"    value={`${potRate}%`}    sub="Trailing avg" />
                <StatCard label="Avg Break (30d)"   value={avgBreak}         sub="This period" />
                <StatCard label="Safety Rate (30d)" value={`${safetyRate}%`} sub="Effective safeties" />
                <StatCard label="All-time High"     value={aggStats?.highBreakAllTime ?? 0} sub="Best break" />
                <StatCard label="Sessions"          value={aggStats?.sessionCount ?? sessionStats.length} sub="Total logged" />
              </div>

              {/* ── Specialty stat cards — only if data exists ── */}
              {hasSpecialty && (
                <div className="u-stats-grid">
                  {overallLongRate !== null && (
                    <StatCard label="Long Pot Rate" value={`${overallLongRate}%`} sub={`${totalLongPots}/${totalLongAttempts} attempts`} />
                  )}
                  {overallRestRate !== null && (
                    <StatCard label="Rest Success" value={`${overallRestRate}%`} sub={`${totalRestPots}/${totalRestAttempts} attempts`} />
                  )}
                  {overallEscapeRate !== null && (
                    <StatCard label="Escape Rate" value={`${overallEscapeRate}%`} sub={`${totalEscapes}/${totalSnookered} snookers`} />
                  )}
                </div>
              )}

              {/* ── Tier standing + gap to next ── */}
              <div className="u-two-col">
                <Panel title="Current Standing">
                  <TierBadge tierName={tier.name} tierColor={tier.color} nextTierName={nextTier?.name} />
                  <p style={{ fontSize: '0.85rem', marginTop: 16 }}>{tier.description}</p>
                  <p style={{ fontSize: '0.75rem', marginTop: 8, fontStyle: 'italic', color: 'var(--chalk-faint)' }}>
                    Equivalent: {tier.proEquivalent}
                  </p>
                </Panel>

                <Panel title={nextTier ? `To reach ${nextTier.name}` : 'Maximum Tier'}>
                  {nextTier ? (
                    <>
                      <GapRow label="Avg Break"   current={avgBreak}   target={nextTier.minAvgBreak}   gap={gaps.avgBreak}   unit=""  color={tier.color} />
                      <GapRow label="Pot Rate"    current={potRate}    target={nextTier.minPotRate}    gap={gaps.potRate}    unit="%" color={tier.color} />
                      <GapRow label="Safety Rate" current={safetyRate} target={nextTier.minSafetyRate} gap={gaps.safetyRate} unit="%" color={tier.color} />
                      <p style={{ fontSize: '0.75rem', marginTop: 14, color: 'var(--chalk-faint)', fontStyle: 'italic' }}>
                        Next: <span style={{ color: nextTier.color }}>{nextTier.name}</span> — {nextTier.description}
                      </p>
                    </>
                  ) : (
                    <p style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>🏆 Maximum tier reached</p>
                  )}
                </Panel>
              </div>

              {/* ── Tier ladder ── */}
              <Panel title="Tier Ladder">
                <div className="tier-ladder">
                  {TIERS.map(t => {
                    const isCurrent = t.name === tier.name;
                    return (
                      <div key={t.slug} className="tier-ladder__item" style={{
                        borderTopColor: isCurrent ? t.color : t.color + '44',
                        background: isCurrent ? `${t.color}18` : 'rgba(255,255,255,0.02)',
                      }}>
                        <div className="tier-ladder__name"  style={{ color: isCurrent ? t.color : 'var(--chalk-faint)' }}>{t.name}</div>
                        <div className="tier-ladder__break" style={{ color: isCurrent ? t.color : 'var(--chalk-faint)' }}>{t.minAvgBreak}+</div>
                        <div className="tier-ladder__unit">avg break</div>
                        {isCurrent && <div className="tier-ladder__you" style={{ color: t.color }}>◆ YOU</div>}
                      </div>
                    );
                  })}
                </div>
              </Panel>

              {/* ── Core charts ── */}
              <div className="u-two-col" style={{ marginTop: 14 }}>
                <Panel title="Pot Rate Over Sessions">
                  <StatChart data={chartData} dataKey="potRate" color="#c9a84c" unit="%" refLine={nextTier?.minPotRate} />
                </Panel>
                <Panel title="High Break Per Session">
                  <StatChart data={chartData} dataKey="highBreak" color="#8e44ad" />
                </Panel>
                <Panel title="Average Break Per Visit">
                  <StatChart data={chartData} dataKey="avgBreak" color="#2980b9" refLine={nextTier?.minAvgBreak} />
                </Panel>
                <Panel title="Safety Rate">
                  <StatChart data={chartData} dataKey="safetyRate" color="#27ae60" unit="%" refLine={nextTier?.minSafetyRate} />
                </Panel>
              </div>

              {/* ── Specialty charts — only shown when data exists ── */}
              {hasSpecialty && (
                <>
                  <div style={{
                    fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'var(--chalk-faint)',
                    marginBottom: 12, marginTop: 4,
                  }}>
                    Specialty Stats
                  </div>
                  <div className="u-two-col">
                    {hasLongData && (
                      <Panel title="Long Pot Rate">
                        <StatChart
                          data={longChartData} dataKey="longPotRate"
                          color="#8e44ad" unit="%" nullAsGap
                        />
                        <p style={{ fontSize: '0.72rem', color: 'var(--chalk-faint)', marginTop: 8, fontStyle: 'italic' }}>
                          Only sessions where you logged a long shot are plotted.
                        </p>
                      </Panel>
                    )}
                    {hasRestData && (
                      <Panel title="Rest Shot Success">
                        <StatChart
                          data={longChartData} dataKey="restSuccessRate"
                          color="#16a085" unit="%" nullAsGap
                        />
                        <p style={{ fontSize: '0.72rem', color: 'var(--chalk-faint)', marginTop: 8, fontStyle: 'italic' }}>
                          Only sessions where you used the rest are plotted.
                        </p>
                      </Panel>
                    )}
                    {hasEscapeData && (
                      <Panel title="Snooker Escape Rate">
                        <StatChart
                          data={longChartData} dataKey="snookerEscapeRate"
                          color="#e67e22" unit="%" nullAsGap
                        />
                        <p style={{ fontSize: '0.72rem', color: 'var(--chalk-faint)', marginTop: 8, fontStyle: 'italic' }}>
                          Only sessions where you were snookered are plotted.
                        </p>
                      </Panel>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════ DRILLS TAB ══════════════ */}
      {tab === 'drills' && (
        <>
          {!hasDrillData ? (
            <EmptyState icon="" title="No drill data yet" subtitle="Complete some drills to see your progress here."
              action={<Button onClick={() => navigate('/drills')}>Go to Drills</Button>} />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
                {drillSummary.map(({ drill, attempts, best, avg, bestTier, nextDrillTier }) => {
                  const config    = DRILL_TIER_CONFIGS[drill.id];
                  const isActive  = drill.id === activeSelected;
                  const DrillIcon = DRILL_ICONS[drill.id];
                  return (
                    <button
                      key={drill.id}
                      onClick={() => setSelectedDrill(drill.id)}
                      style={{
                        background: isActive ? `${bestTier?.color ?? 'var(--gold)'}12` : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${isActive ? (bestTier?.color ?? 'var(--gold)') : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 10, padding: '16px', textAlign: 'left',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <DrillIcon size={16} strokeWidth={1.75} color={bestTier?.color ?? 'var(--chalk-dim)'} />
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--chalk)', letterSpacing: '-0.01em' }}>{drill.name}</span>
                      </div>
                      {bestTier && (
                        <div style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          background: bestTier.color + '22', border: `1px solid ${bestTier.color}55`,
                          color: bestTier.color, fontSize: '0.6rem', fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10,
                        }}>
                          {bestTier.label}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: bestTier?.color ?? 'var(--gold)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {best ?? '—'}{config.scoreType === 'safety_pct' ? '%' : ''}
                          </div>
                          <div style={{ fontSize: '0.55rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)' }}>Best</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: 'var(--chalk-dim)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {avg ?? '—'}{config.scoreType === 'safety_pct' ? '%' : ''}
                          </div>
                          <div style={{ fontSize: '0.55rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)' }}>Avg</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: 'var(--chalk-dim)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            {attempts}
                          </div>
                          <div style={{ fontSize: '0.55rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)' }}>Runs</div>
                        </div>
                      </div>
                      {nextDrillTier && best !== null && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min(Math.round((best / nextDrillTier.minScore) * 100), 100)}%`,
                              background: `linear-gradient(to right, ${bestTier?.color ?? 'var(--gold)'}88, ${nextDrillTier.color})`,
                              borderRadius: 3, transition: 'width 0.8s ease',
                            }} />
                          </div>
                          <div style={{ fontSize: '0.55rem', color: 'var(--chalk-faint)', marginTop: 3 }}>
                            {nextDrillTier.minScore - best} to {nextDrillTier.label}
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {activeDrillData && (
                <Panel title={`${activeDrillData.drill.name} — Score Over Time`}>
                  {activeDrillChart.length < 2 ? (
                    <p style={{ fontSize: '0.8rem', color: 'var(--chalk-faint)', fontStyle: 'italic' }}>
                      Complete more attempts to see your trend line.
                    </p>
                  ) : (
                    <StatChart
                      data={activeDrillChart}
                      dataKey="score"
                      color={activeDrillData.bestTier?.color ?? '#c9a84c'}
                      unit={DRILL_TIER_CONFIGS[activeDrillData.drill.id].scoreType === 'safety_pct' ? '%' : ''}
                      xKey="attempt"
                    />
                  )}
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

function GapRow({ label, current, target, gap, unit, color }: {
  label: string; current: number; target: number; gap: number; unit: string; color: string;
}) {
  const pct = Math.min(Math.round((current / Math.max(target, 1)) * 100), 100);
  return (
    <div className="progress-row">
      <div className="progress-row__labels">
        <span className="progress-row__label">{label}</span>
        <span className="progress-row__label" style={{ color: gap === 0 ? 'var(--success)' : undefined }}>
          {gap === 0 ? '✓ Done' : `Need +${gap}${unit}`}
        </span>
      </div>
      <div className="progress-row__track">
        <div className="progress-row__fill" style={{
          width: `${pct}%`,
          background: gap === 0 ? 'var(--success)' : `linear-gradient(to right, ${color}88, ${color})`,
        }} />
      </div>
      <div className="progress-row__hint">{current}{unit} of {target}{unit} needed</div>
    </div>
  );
}

function StatChart({ data, dataKey, color, unit = '', refLine, xKey = 'session', nullAsGap = false }: {
  data: any[]; dataKey: string; color: string; unit?: string;
  refLine?: number; xKey?: string; nullAsGap?: boolean;
}) {
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey={xKey} tick={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: '#6b6358' }} tickLine={false} />
        <YAxis tick={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: '#6b6358' }} tickLine={false} />
        <Tooltip
          contentStyle={{ background: '#071f16', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}
          labelStyle={{ color: '#b8b0a0' }}
          itemStyle={{ color }}
          formatter={(v: number) => v !== null ? [`${v}${unit}`, dataKey] : [null, dataKey]}
        />
        {refLine !== undefined && <ReferenceLine y={refLine} stroke="rgba(201,168,76,0.4)" strokeDasharray="4 4" />}
        <Line
          type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2}
          dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 5, fill: color }}
          connectNulls={!nullAsGap}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}